import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import userflow from 'userflow.js';
import { configSettings } from '@/lib/config';
import { USERFLOW_TOKENS } from '@/lib/constants';
import { User } from '@/types/user';
import { buildUserflowAttributes, mapUserToUserflowData } from '@/lib/userflow';

declare global {
    interface Window {
        userflow?: typeof userflow;
    }
}

interface UserflowContextValue {
    identifyInUserflow: (user: User) => void;
    updateUser: (attributes: Record<string, any>) => void;
    updateUserAttribute: (key: string, value: any) => void;
    track: (eventName: string, properties?: Record<string, any>) => void;
    isIdentified: () => boolean;
    startFlow: (flowId: string) => void;
    reidentifyInUserflow: (user: User) => void;
}

const UserflowContext = createContext<UserflowContextValue | null>(null);

/**
 * Provider that initializes Userflow and provides tracking functions
 */
export function UserflowProvider({ children }: { children: React.ReactNode }) {
    // Initialize Userflow once on mount
    useEffect(() => {
        try {
            const token =
                configSettings.environment === 'production'
                    ? process.env.NEXT_PUBLIC_USERFLOW_TOKEN_PRODUCTION || USERFLOW_TOKENS.PRODUCTION
                    : process.env.NEXT_PUBLIC_USERFLOW_TOKEN_STAGING || USERFLOW_TOKENS.STAGING;

            userflow.init(token);
            window.userflow = userflow;
        } catch (error) {
            console.error('Failed to initialize Userflow:', error);
        }
    }, []);

    const identifyInUserflow = useCallback((user: User) => {
        if (!user) return;

        try {
            const userData = mapUserToUserflowData(user);
            userflow.identify(user.user_id.toString(), userData);

            const attributes = buildUserflowAttributes(user);
            if (Object.keys(attributes).length > 0) {
                userflow.updateUser(attributes);
            }
        } catch (error) {
            console.error('Failed to identify user in Userflow:', error);
        }
    }, []);

    const updateUser = useCallback((attributes: Record<string, any>) => {
        try {
            userflow.updateUser(attributes);
        } catch (error) {
            console.error('Failed to update user in Userflow:', error);
        }
    }, []);

    const updateUserAttribute = useCallback((key: string, value: any) => {
        try {
            userflow.updateUser({ [key]: value });
        } catch (error) {
            console.error('Failed to update user attribute in Userflow:', error);
        }
    }, []);

    const track = useCallback((eventName: string, properties?: Record<string, any>) => {
        try {
            if (userflow.isIdentified()) {
                userflow.track(eventName, properties);
            }
        } catch (error) {
            console.error('Failed to track event in Userflow:', error);
        }
    }, []);

    const isIdentified = useCallback(() => {
        try {
            return userflow.isIdentified();
        } catch {
            return false;
        }
    }, []);

    const startFlow = useCallback((flowId: string) => {
        try {
            if (userflow.isIdentified()) {
                userflow.start(flowId);
            }
        } catch (error) {
            console.error('Failed to start Userflow:', error);
        }
    }, []);

    const reidentifyInUserflow = useCallback((user: User) => {
        if (!user) return;

        try {
            const userData = mapUserToUserflowData(user);
            userflow.identify(user.user_id.toString(), userData);
        } catch (error) {
            console.error('Failed to re-identify user in Userflow:', error);
        }
    }, []);

    const value = useMemo<UserflowContextValue>(
        () => ({
            identifyInUserflow,
            updateUser,
            updateUserAttribute,
            track,
            isIdentified,
            startFlow,
            reidentifyInUserflow,
        }),
        [identifyInUserflow, updateUser, updateUserAttribute, track, isIdentified, startFlow, reidentifyInUserflow]
    );

    return <UserflowContext.Provider value={value}>{children}</UserflowContext.Provider>;
}

/**
 * Hook to access Userflow functions
 * Must be used within UserflowProvider
 */
export function useUserflow() {
    const context = useContext(UserflowContext);
    if (!context) {
        throw new Error('useUserflow must be used within UserflowProvider');
    }
    return context;
}
